import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";

import { useNavigate, useParams, useLocation } from "react-router-dom";

import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import { LEAGUES } from "../data/leaguesConfig";
import { sportsDbFetch } from "../utils/sportsDbCache";
import webPlayerStorage from "../utils/webPlayerStorage";

const getLeagueRoute = (league) => {
  const sportsDbId = Number(league.idLeague);
  const configMatch = LEAGUES.find((l) => l.sportsDbId === sportsDbId);

  if (configMatch) {
    return `/web-player/sports/league/${configMatch.id}`;
  }

  return `/web-player/sports/league/${league.idLeague}`;
};

const SPORT_SLUG_MAP = {
  football: "Soccer",
  basketball: "Basketball",
  "american-football": "American Football",
  baseball: "Baseball",
  hockey: "Ice Hockey",
  motorsports: "Motorsport",
  fighting: "Fighting",
  rugby: "Rugby",
  volleyball: "Volleyball",
  afl: "Australian Football",
  cricket: "Cricket",
  "racket-sports": "Tennis",
  golf: "Golf",
  other: "Other",
  nfl: "American Football",
  ncaaf: "American Football",
  nba: "Basketball",
  nhl: "Ice Hockey",
  mlb: "Baseball",
  soccer: "Soccer",
  "ice-hockey": "Ice Hockey",
  motorsport: "Motorsport",
};

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
  { key: "name", label: "Name A-Z", icon: "fa-sort-alpha-down" },
  { key: "country", label: "Country", icon: "fa-globe" },
  { key: "founded", label: "Founded", icon: "fa-calendar" },
];

// ─── LEAGUE CARD ───
const LeagueGridCard = ({ league, navigate }) => (
  <button
    className="bg-white/[0.04] rounded-xl p-5 border border-white/[0.08] hover:border-blue-500/40 transition-all text-center group"
    onClick={() => navigate(getLeagueRoute(league))}
  >
    {league.strBadge || league.strLogo ? (
      <img
        alt={league.strLeague}
        className="w-14 h-14 object-contain mx-auto mb-3 rounded-lg"
        src={league.strBadge || league.strLogo}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    ) : (
      <div className="w-14 h-14 rounded-lg bg-blue-600/20 flex items-center justify-center mx-auto mb-3">
        <span className="text-sm font-bold text-blue-400">
          {(league.strLeague || "?").substring(0, 3)}
        </span>
      </div>
    )}
    <h4 className="text-white text-sm font-bold truncate group-hover:text-blue-300 transition-colors">
      {league.strLeague}
    </h4>
    <p className="text-white/30 text-[11px] mt-1">{league.strCountry}</p>
    {league.strCurrentSeason && (
      <p className="text-green-400/60 text-[10px] mt-1 font-semibold">
        {league.strCurrentSeason}
      </p>
    )}
  </button>
);

// ─── MAIN COMPONENT ───
const WebPlayerSportHubAll = () => {
  const { sportName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasCheckedProfile = useRef(false);
  const loaderRef = useRef(null);

  const [allLeagues, setAllLeagues] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const searchInputRef = useRef(null);

  const profile = webPlayerStorage.getProfile();
  const playlist = webPlayerStorage.getPlaylist();

  const slugKey = sportName?.toLowerCase();
  const sport = SPORT_SLUG_MAP[slugKey] || sportName || "Sports";
  const category = location.state?.category || "All Leagues";
  const passedLeagues = location.state?.leagues;

  const fetchAllLeagues = useCallback(async () => {
    setLoading(true);
    try {
      if (passedLeagues && passedLeagues.length > 0) {
        setAllLeagues(passedLeagues);
      } else {
        const data = await sportsDbFetch(
          `search_all_leagues.php?s=${encodeURIComponent(sport)}`
        );
        const leagues =
          data?.countries || data?.countrys || data?.leagues || [];

        setAllLeagues(leagues);
      }
    } catch (err) {
      console.error("Failed to fetch leagues:", err);
    } finally {
      setLoading(false);
    }
  }, [sport, passedLeagues]);

  useEffect(() => {
    if (!hasCheckedProfile.current) {
      hasCheckedProfile.current = true;
      const p = webPlayerStorage.getProfile();

      if (!p || !p.id) {
        navigate("/web-player/profiles");

        return;
      }
    }
    fetchAllLeagues();
  }, [fetchAllLeagues, navigate]);

  // Filter + sort
  const filteredSorted = useMemo(() => {
    let result = [...allLeagues];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();

      result = result.filter(
        (l) =>
          (l.strLeague || "").toLowerCase().includes(q) ||
          (l.strCountry || "").toLowerCase().includes(q) ||
          (l.strLeagueAlternate || "").toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortBy === "name") {
      result.sort((a, b) =>
        (a.strLeague || "").localeCompare(b.strLeague || "")
      );
    } else if (sortBy === "country") {
      result.sort(
        (a, b) =>
          (a.strCountry || "").localeCompare(b.strCountry || "") ||
          (a.strLeague || "").localeCompare(b.strLeague || "")
      );
    } else if (sortBy === "founded") {
      result.sort(
        (a, b) =>
          (Number(b.intFormedYear) || 0) - (Number(a.intFormedYear) || 0)
      );
    }

    return result;
  }, [allLeagues, searchQuery, sortBy]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery, sortBy]);

  // Infinite scroll observer
  useEffect(() => {
    if (visibleCount >= filteredSorted.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, filteredSorted.length)
          );
        }
      },
      { threshold: 0.1 }
    );

    const el = loaderRef.current;

    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [visibleCount, filteredSorted.length]);

  const visibleLeagues = filteredSorted.slice(0, visibleCount);

  // Group by country when sorted by country
  const groupedByCountry = useMemo(() => {
    if (sortBy !== "country") return null;
    const groups = {};

    visibleLeagues.forEach((l) => {
      const country = l.strCountry || "Unknown";

      if (!groups[country]) groups[country] = [];
      groups[country].push(l);
    });

    return groups;
  }, [visibleLeagues, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4" />
          <p className="text-white/40 text-sm">Loading leagues...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <LeftMenu
        isSportsPage
        activeProfile={profile}
        playlist={playlist}
        selectedSection="sports"
      />
      <MobileMenu activeProfile={profile} playlist={playlist} />

      <div className="ml-0 md:ml-[72px]">
        {/* Header */}
        <div className="px-6 md:px-12 pt-8 pb-6">
          <div className="flex items-center gap-4 mb-6">
            <button
              className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left text-white" />
            </button>
            <div>
              <div className="text-white/40 text-xs font-medium tracking-wider mb-1">
                <button
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                  onClick={() => navigate("/web-player/sports")}
                >
                  Sports
                </button>
                <span className="mx-2">&rsaquo;</span>
                <button
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                  onClick={() =>
                    navigate(`/web-player/sports/hub/${sportName}`)
                  }
                >
                  {sport}
                </button>
                <span className="mx-2">&rsaquo;</span>
                {category}
              </div>
              <h1 className="text-3xl font-extrabold tracking-tight">
                {category}
              </h1>
            </div>
          </div>

          {/* Search + Sort bar */}
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-white/20 text-sm" />
              <input
                className="w-full pl-10 pr-10 py-2.5 bg-white/[0.06] border border-white/[0.08] rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-500/40 transition-colors"
                placeholder="Search leagues or countries..."
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                >
                  <i className="fas fa-times text-sm" />
                </button>
              )}
            </div>

            {/* Sort pills */}
            <div className="flex gap-1.5">
              {SORT_OPTIONS.map((opt) => (
                <button
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                    sortBy === opt.key
                      ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                      : "bg-white/[0.06] text-white/40 border border-white/[0.06] hover:bg-white/10 hover:text-white/60"
                  }`}
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                >
                  <i className={`fas ${opt.icon} text-[10px]`} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Results count */}
          <p className="text-white/40 text-sm">
            {searchQuery ? (
              <>
                <span className="text-blue-400 font-semibold">
                  {filteredSorted.length}
                </span>{" "}
                results for &quot;{searchQuery}&quot;
              </>
            ) : (
              <>
                Showing{" "}
                <span className="text-blue-400 font-semibold">
                  {visibleLeagues.length}
                </span>{" "}
                of{" "}
                <span className="text-blue-400 font-semibold">
                  {allLeagues.length}
                </span>{" "}
                leagues
              </>
            )}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 md:px-12 pb-12">
          {filteredSorted.length === 0 ? (
            <div className="text-center py-16">
              <i className="fas fa-search text-3xl text-white/10 mb-4" />
              <p className="text-white/30 text-sm">
                No leagues found for &quot;{searchQuery}&quot;
              </p>
              <button
                className="mt-3 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                onClick={() => setSearchQuery("")}
              >
                Clear search
              </button>
            </div>
          ) : sortBy === "country" && groupedByCountry ? (
            // Grouped by country view
            Object.entries(groupedByCountry).map(([country, leagues]) => (
              <div className="mb-8" key={country}>
                <div className="flex items-center gap-3 mb-4">
                  <i className="fas fa-globe text-white/15 text-xs" />
                  <h3 className="text-sm font-bold text-white/50 uppercase tracking-wider">
                    {country}
                  </h3>
                  <span className="text-[10px] text-white/20 font-semibold">
                    {leagues.length}
                  </span>
                  <div className="flex-1 h-px bg-white/[0.06]" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {leagues.map((league) => (
                    <LeagueGridCard
                      key={league.idLeague}
                      league={league}
                      navigate={navigate}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            // Flat grid view
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {visibleLeagues.map((league) => (
                <LeagueGridCard
                  key={league.idLeague}
                  league={league}
                  navigate={navigate}
                />
              ))}
            </div>
          )}

          {/* Lazy load trigger */}
          {visibleCount < filteredSorted.length && (
            <div
              className="flex items-center justify-center py-10"
              ref={loaderRef}
            >
              <i className="fas fa-spinner fa-spin text-2xl text-blue-500/50" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebPlayerSportHubAll;
